import { useEffect, useRef, useState, useCallback } from "react";
import Backend from "../../../../Backend/Backend";
import Select from "react-select";
import { FiSearch } from "react-icons/fi";
import toast from "react-hot-toast";
import moment from "moment";

const backend = await new Backend().init();

export default function Filter({
  userType,
  records = 0,
  clear,
  search,
  onFilter,
}) {
  const searchRef = useRef();

  const [superList, setSuperList] = useState([]);
  const [masterList, setMasterList] = useState([]);
  const [userList, setUserList] = useState([]);

  const [superBackup, setSuperBackup] = useState([]);
  const [masterBackup, setMasterBackup] = useState([]);
  const [userBackup, setUserBackup] = useState([]);

  const [superId, setSuperId] = useState(null);
  const [masterId, setMasterId] = useState(null);
  const [userId, setUserId] = useState(null);

  const [start, setStart] = useState(
    moment().startOf("week").format("YYYY-MM-DD"),
  );
  const [end, setEnd] = useState(moment().endOf("week").format("YYYY-MM-DD"));

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchFilterData = useCallback(
    async (params, setList, setBackup) => {
      setLoading(true);
      try {
        const res = await backend.user_filter({
          server: localStorage.getItem("server"),
          id: localStorage.getItem("id"),
          username: localStorage.getItem("username"),
          token: localStorage.getItem("token"),
          start,
          end,
          pending: "1",
          ...params,
        });

        if (res.error === "False") {
          const formatted = res.user.map((u) => ({
            value: JSON.stringify(u),
            label: u.super_username || u.master_username || u.username,
          }));

          setList(formatted);
          setBackup(formatted);
        } else {
          toast.error(res.message || "Failed to load filter data");
        }
      } catch (err) {
        toast.error("Error loading filter data");
      } finally {
        setLoading(false);
      }
    },
    [start, end],
  );

  useEffect(() => {
    const id = localStorage.getItem("id");

    if (userType === 1)
      fetchFilterData({ admin_id: id }, setSuperList, setSuperBackup);
    if (userType === 2)
      fetchFilterData({ super_id: id }, setMasterList, setMasterBackup);
    if (userType === 3)
      fetchFilterData({ master_id: id }, setUserList, setUserBackup);
    if (userType === 5)
      fetchFilterData({ broker_id: id }, setUserList, setUserBackup);
  }, [userType, fetchFilterData]);

  const handleFilter = () => {
    try {
      onFilter?.({
        super_id: superId ? JSON.parse(superId.value).super_id : null,
        master_id: masterId ? JSON.parse(masterId.value).master_id : null,
        user_id: userId ? JSON.parse(userId.value).user_id : null,
        start,
        end,
      });
    } catch {
      toast.error("Invalid selection");
    }
  };

  const handleClear = () => {
    clear?.();
    setSuperId(null);
    setMasterId(null);
    setUserId(null);

    setSuperList(superBackup);
    setMasterList(masterBackup);
    setUserList(userBackup);
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 16px",
        background: "var(--background)",
        color: "var(--text)",
      }}
    >
      {/* LEFT SIDE */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <div
          className="px-3 py-1 rounded-full text-sm font-medium"
          style={{
            background: "var(--background)",
            border: "2px solid var(--hover)",
            color: "var(--text)",
          }}
        >
          Records: {records || 0}
        </div>

        <input
          ref={searchRef}
          type="text"
          placeholder="Search Symbol"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            search?.(e.target.value);
          }}
          className="w-[180px] px-4 py-1 rounded-full text-sm focus:ring-2 outline-none"
          style={{
            background: "var(--background)",
            border: "2px solid var(--hover)",
            color: "var(--text)",
          }}
        />
      </div>

      {/* RIGHT SIDE */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        {userType === 1 && (
          <RenderSelect
            placeholder="Select Super"
            value={superId}
            options={superList}
            setter={setSuperId}
            idKey="super_id"
            next={(id) =>
              fetchFilterData({ super_id: id }, setMasterList, setMasterBackup)
            }
          />
        )}

        {(userType === 1 || userType === 2) && (
          <RenderSelect
            placeholder="Select Master"
            value={masterId}
            options={masterList}
            setter={setMasterId}
            idKey="master_id"
            next={(id) =>
              fetchFilterData({ master_id: id }, setUserList, setUserBackup)
            }
          />
        )}

        {(userType === 1 ||
          userType === 2 ||
          userType === 3 ||
          userType === 5) && (
          <RenderSelect
            placeholder="Select User"
            value={userId}
            options={userList}
            setter={setUserId}
          />
        )}

        {/* DATE PICKERS */}
        <input
          type="date"
          value={start}
          max={moment().format("YYYY-MM-DD")}
          onChange={(e) => setStart(e.target.value)}
          style={{
            background: "var(--background)",
            border: "2px solid var(--hover)",
            color: "var(--text)",
            padding: "6px 10px",
            borderRadius: "6px",
          }}
        />

        <input
          type="date"
          value={end}
          max={moment().format("YYYY-MM-DD")}
          onChange={(e) => setEnd(e.target.value)}
          style={{
            background: "var(--background)",
            border: "2px solid var(--hover)",
            color: "var(--text)",
            padding: "6px 10px",
            borderRadius: "6px",
          }}
        />

        {/* FILTER BTN */}
        <button
          disabled={loading}
          onClick={handleFilter}
          className="px-4 py-1.5 bg-[var(--primary)] text-white rounded-full text-sm font-semibold shadow"
        >
          Filter
        </button>

        {/* CLEAR BTN */}
        <button
          disabled={loading}
          onClick={handleClear}
          className="px-4 py-1.5 bg-[var(--sell)] text-white rounded-full text-sm font-semibold shadow"
        >
          Clear
        </button>
      </div>
    </div>
  );
}

const RenderSelect = ({ placeholder, value, options, setter, next, idKey }) => (
  <div className="relative" style={{ width: 200 }}>
    <FiSearch
      className="absolute left-3 top-1/2 -translate-y-1/2 text-[18px] z-10"
      style={{ color: "var(--text)" }}
    />

    <Select
      width="200px"
      placeholder={placeholder}
      value={value}
      options={options}
      onChange={(val) => {
        setter(val);
        if (val && next) {
          const id = JSON.parse(val.value)[idKey];
          next(id);
        }
      }}
      styles={selectStyles}
    />
  </div>
);

// React Select Theme Style
const selectStyles = {
  control: (base) => ({
    ...base,
    borderRadius: "30px",
    minHeight: "36px",
    backgroundColor: "var(--hover)",
    border: "none",
    paddingLeft: "25px",
    boxShadow: "none",
    color: "var(--text)",
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "var(--background)",
    borderRadius: "8px",
    border: "1px solid var(--hover)",
    padding: "4px",
    color: "var(--text)",
  }),
  option: (base, { isFocused }) => ({
    ...base,
    backgroundColor: isFocused ? "var(--primary)" : "transparent",
    color: isFocused ? "var(--white)" : "var(--text)",
    cursor: "pointer",
  }),
  singleValue: (base) => ({
    ...base,
    color: "var(--text)",
  }),
  input: (base) => ({
    ...base,
    color: "var(--text)",
  }),
  placeholder: (base) => ({
    ...base,
    color: "var(--text)",
    opacity: 0.6,
  }),
};
